import React, { useMemo } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import { useResolvedThemeMode } from "src/hooks/useResolvedThemeMode";

// @mui/x-data-grid and @mui/material's Menu/MenuItem/IconButton are styled
// via MUI's own ThemeProvider/emotion, not CoreUI's CSS custom properties.
// Bridges our system|light|dark choice (useTheme.js) into a real MUI
// palette.mode so these components actually go dark instead of staying a
// hardcoded-light island inside an otherwise-dark page.
const MuiThemeBridge = ({ children }) => {
  const mode = useResolvedThemeMode();
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          // Setting only `mode` leaves background.paper/default on MUI's
          // own generic dark-gray defaults, not this app's --app-surface/
          // --app-bg tokens -- confirmed by reading @mui/material's Paper
          // source: it also layers a backgroundImage:linear-gradient(...)
          // white overlay on top of background.paper, scaled by elevation
          // (MUIDataTable renders at elevation4), which is why the table
          // read as a visibly different, lighter shade than the rest of
          // the dark-mode app. Pointing both at the real tokens and
          // killing the overlay below (this app has no other elevation-
          // based surfaces to stay consistent with).
          //
          // These must be real hex values, NOT the var(--app-*) CSS custom
          // property strings used everywhere else in this bridge (those are
          // fine in styleOverrides, which just hands the string straight to
          // CSS) -- confirmed the hard way: @mui/x-data-grid's own internal
          // GridRootStyles.js calls MUI's alpha() on theme.palette.background
          // .default to compute a derived color, and alpha()/decomposeColor()
          // parse the value in JS, synchronously throwing ("Minified MUI
          // error #9") on a var(...) string since it can't decompose into
          // r/g/b. Mirrors theme.css's --app-bg/--app-surface light+dark
          // values directly; keep these in sync if those tokens ever change.
          background: {
            paper: mode === "dark" ? "#152228" : "#FFFFFF",
            default: mode === "dark" ? "#0F1A20" : "#F6F5F2",
          },
        },
        // MUI defaults to its own Roboto-first font stack unless told
        // otherwise -- nothing in this app loads Roboto, so every MUI
        // component (mui-datatables and DataGrid alike) was silently
        // falling back to generic sans-serif, visibly different from the
        // rest of the app's actual font. "inherit" picks up whatever's
        // already cascading from body, same system stack CoreUI uses
        // everywhere else -- safe, pure CSS, no color/JS math involved.
        typography: { fontFamily: "inherit" },
        // DataGrid and MUI's Menu/MenuItem/IconButton (the attendance pages'
        // Breaks/Action popovers) are the only MUI components left reading
        // this theme -- DataGrid styles itself per-file via gridSx, and
        // Menu's dropdown renders on a Paper surface, which is what the
        // override below is for. CSS custom property strings (var(--app-*))
        // work directly as styleOverrides values -- MUI emits them as plain
        // CSS at runtime, same as confirmed for react-select's styles prop
        // elsewhere.
        components: {
          // Kills MUI's elevation overlay gradient (a lightening wash Paper
          // adds on top of background.paper, scaled by elevation) so Menu's
          // dropdown surface is the flat --app-surface/--app-surface-soft
          // tone the rest of the app uses, not that tone plus a wash.
          MuiPaper: {
            styleOverrides: {
              root: { backgroundImage: "none" },
            },
          },
        },
      }),
    [mode]
  );

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
};

export default MuiThemeBridge;
